import React, { useEffect, useState } from 'react';
import { MessageSquare, MoveHorizontal, Plus, RotateCcw } from 'lucide-react';
import { useDwellClock } from '../capture';
import {
  ADDABLE_CLASSES, addFinding, adjustBounds, Capture, formatStudyTime, PriorAddition, ReviewDelta, TriageItem,
} from '../review';

/**
 * What a reviewer knows beyond confirm / reject / reclassify (STRATEGY.md 2026-10-02, pillar 3):
 * a reason, corrected bounds, a finding the engine missed, and the additions an earlier analysis
 * carried. Every action goes through the workspace's `act`, so locking, merging and errors behave
 * exactly as they do for the keyboard verdicts.
 */

interface Props {
  studyId: string;
  selected: TriageItem | null;
  viewStart: number;
  viewEnd: number;
  durationSeconds: number;
  editable: boolean;
  busy: boolean;
  reason: string;
  onReason: (text: string) => void;
  /** Time on the selected item so far — the workspace's clock, shared with the keyboard verdicts. */
  dwell: () => number;
  priorAdditions: PriorAddition[];
  /** Resolves true when the action was applied, false when it was refused (the workspace shows why). */
  act: (fn: () => Promise<ReviewDelta>, advance?: boolean) => Promise<boolean>;
  onFocusSpan: (startSeconds: number, endSeconds: number) => void;
}

const NUDGE = 0.2;

function BoundsField({
  label, value, onChange, fromView, fromViewLabel,
}: { label: string; value: number; onChange: (v: number) => void; fromView: number; fromViewLabel: string }) {
  const btn = 'rounded border border-slate-700 px-1.5 py-0.5 text-slate-300 hover:border-slate-500';
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-12 text-slate-400">{label}</span>
      <button className={btn} title={`${NUDGE} s earlier`} onClick={() => onChange(Math.max(0, value - NUDGE))}>−</button>
      <span className="mono min-w-[7.5rem] text-center text-slate-100">{formatStudyTime(value)}.{Math.floor((value % 1) * 10)}</span>
      <button className={btn} title={`${NUDGE} s later`} onClick={() => onChange(value + NUDGE)}>+</button>
      <button className={btn} onClick={() => onChange(fromView)}>{fromViewLabel}</button>
    </div>
  );
}

export const CapturePanel: React.FC<Props> = ({
  studyId, selected, viewStart, viewEnd, durationSeconds, editable, busy, reason, onReason, dwell,
  priorAdditions, act, onFocusSpan,
}) => {
  const [mode, setMode] = useState<'none' | 'bounds' | 'add'>('none');
  const [onset, setOnset] = useState(0);
  const [offset, setOffset] = useState(0);
  const [addClass, setAddClass] = useState<string>('svt');
  // The add form has its own clock: time spent finding a missed event is the cost being measured.
  const addDwell = useDwellClock(mode === 'add' ? 'add' : null);

  useEffect(() => {
    // A bounds edit is about one finding; switching findings abandons it.
    if (mode === 'bounds') setMode('none');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const clamp = (v: number) => Math.max(0, Math.min(durationSeconds, v));
  const valid = offset > onset;
  const capture = (ms: number): Capture => ({ comment: reason, dwellMs: ms });

  const openBounds = () => {
    if (!selected) return;
    setOnset(selected.onset_seconds);
    setOffset(selected.offset_seconds);
    setMode('bounds');
  };
  const openAdd = () => {
    setOnset(viewStart);
    setOffset(viewEnd);
    setMode('add');
  };

  const btn = 'flex items-center gap-1 rounded border px-2 py-1 disabled:opacity-40';
  return (
    <div className="glass-panel space-y-2 rounded-xl px-4 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="verdict-reason" className="flex items-center gap-1 text-slate-400">
          <MessageSquare className="h-3.5 w-3.5" /> Reason
        </label>
        <input
          id="verdict-reason"
          value={reason}
          maxLength={500}
          disabled={!editable}
          onChange={(e) => onReason(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') (e.target as HTMLInputElement).blur(); }}
          placeholder="Optional — sent with your next verdict. Why, in a few words (no patient details)."
          className="min-w-[16rem] flex-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-100 placeholder:text-slate-600 disabled:opacity-40"
        />
        <button disabled={!editable || busy || !selected} onClick={mode === 'bounds' ? () => setMode('none') : openBounds}
          className={`${btn} border-violet-700 text-violet-200`} title="Move this finding's onset or offset">
          <MoveHorizontal className="h-3.5 w-3.5" /> Edit bounds
        </button>
        <button disabled={!editable || busy} onClick={mode === 'add' ? () => setMode('none') : openAdd}
          className={`${btn} border-sky-700 text-sky-200`} title="Add a finding the engine missed, starting from the visible window">
          <Plus className="h-3.5 w-3.5" /> Add missed finding
        </button>
      </div>

      {mode === 'bounds' && selected && (
        <div className="space-y-1.5 rounded-lg border border-violet-900 bg-violet-950/40 p-2">
          <p className="text-violet-200">
            Bounds of this <strong className="uppercase">{selected.rhythm_class}</strong>. The class stays; the
            engine's bounds are kept with your correction.
          </p>
          <BoundsField label="Onset" value={onset} onChange={(v) => setOnset(clamp(v))} fromView={viewStart} fromViewLabel="= view start" />
          <BoundsField label="Offset" value={offset} onChange={(v) => setOffset(clamp(v))} fromView={viewEnd} fromViewLabel="= view end" />
          <div className="flex gap-2">
            <button className={`${btn} border-slate-600 text-slate-300`} onClick={() => setMode('none')}>Cancel</button>
            <button
              disabled={!valid || busy}
              className={`${btn} border-violet-600 bg-violet-900 font-semibold text-white`}
              onClick={() => void act(() => adjustBounds(studyId, selected.id, onset, offset, capture(dwell()))).then((ok) => ok && setMode('none'))}
            >
              Save bounds ({(offset - onset).toFixed(1)} s)
            </button>
          </div>
        </div>
      )}

      {mode === 'add' && (
        <div className="space-y-1.5 rounded-lg border border-sky-900 bg-sky-950/40 p-2">
          <p className="text-sky-200">
            A finding the engine did not propose. It counts in the report and is marked as added by you.
          </p>
          <div className="flex items-center gap-1.5">
            <label htmlFor="add-finding-class" className="w-12 text-slate-400">Class</label>
            <select id="add-finding-class" value={addClass} onChange={(e) => setAddClass(e.target.value)}
              className="rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-slate-100">
              {ADDABLE_CLASSES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <BoundsField label="Onset" value={onset} onChange={(v) => setOnset(clamp(v))} fromView={viewStart} fromViewLabel="= view start" />
          <BoundsField label="Offset" value={offset} onChange={(v) => setOffset(clamp(v))} fromView={viewEnd} fromViewLabel="= view end" />
          <div className="flex gap-2">
            <button className={`${btn} border-slate-600 text-slate-300`} onClick={() => setMode('none')}>Cancel</button>
            <button
              disabled={!valid || busy}
              className={`${btn} border-sky-600 bg-sky-900 font-semibold text-white`}
              onClick={() => void act(() => addFinding(studyId, addClass, onset, offset, capture(addDwell()))).then((ok) => ok && setMode('none'))}
            >
              Add {addClass.replace(/_/g, ' ')} ({(offset - onset).toFixed(1)} s)
            </button>
          </div>
        </div>
      )}

      {priorAdditions.length > 0 && (
        <div className="space-y-1 rounded-lg border border-amber-900 bg-amber-950/40 p-2 text-amber-100">
          <p>
            {priorAdditions.length} finding(s) added by a reviewer on an earlier analysis are not in this one.
            Look at each, then re-add it or leave it out.
          </p>
          <ul className="space-y-1">
            {priorAdditions.map((p) => (
              <li key={p.label_id} className="flex flex-wrap items-center gap-2">
                <button className="underline" onClick={() => onFocusSpan(p.start_seconds, p.end_seconds)}>
                  <strong className="uppercase">{p.rhythm_class ?? 'unknown'}</strong> {formatStudyTime(p.start_seconds)} ·{' '}
                  {(p.end_seconds - p.start_seconds).toFixed(1)} s
                </button>
                <span className="text-amber-300/70">engine {p.engine_version}{p.actor_role ? ` · ${p.actor_role.toLowerCase()}` : ''}</span>
                <button
                  disabled={!editable || busy || !p.rhythm_class}
                  className={`${btn} border-amber-700 text-amber-100`}
                  onClick={() => void act(() => addFinding(studyId, p.rhythm_class!, p.start_seconds, p.end_seconds, capture(dwell()), p.label_id))}
                >
                  <RotateCcw className="h-3 w-3" /> Re-add
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
